import { AppliedFilter, BOOLEAN_UNSET, FilterPreset, FilterSchema, Operator, operatorLabels } from './types';
import { FaTimes } from 'react-icons/fa';
import CurrencyInput from '../CurrencyInput';

interface Props {
    filter: AppliedFilter;
    schema: FilterSchema;
    onChange: (filter: AppliedFilter) => void;
    onSubmit?: () => void;
    onRemove: () => void;
}

/* O valor de dinheiro fica no filtro como texto com ponto ("1234.56"), que é o que a URL leva; o
   CurrencyInput trabalha em número. Zero vira vazio: 0,00 é o campo sem nada digitado, não um
   filtro por valor zero. */
const asAmount = (value: unknown): number => Number(value) || 0;
const fromAmount = (amount: number): string => (amount ? String(amount) : '');

const MoneyInput: React.FC<{
    label: string;
    value: unknown;
    inputClassName: string;
    onAmount: (amount: number) => void;
}> = ({ label, value, inputClassName, onAmount }) => (
    <CurrencyInput label="" aria-label={label} className="mb-0" fractionDigits={2}
        inputClassName={inputClassName} value={asAmount(value)} onChange={onAmount} />
);

/** O preset está valendo? Compara operador + valor com o que ele produziria AGORA. */
const isPresetActive = (preset: FilterPreset, filter: AppliedFilter): boolean => {
    const { operator, value } = preset.apply();
    if (operator !== filter.operator) return false;
    return JSON.stringify(value) === JSON.stringify(filter.value);
};

const FilterField: React.FC<Props> = ({ filter, schema, onChange, onRemove, onSubmit }) => {
    const updateOperator = (op: Operator) => onChange({
        ...filter,
        operator: op,
        // no booleano quem responde é o operador, e o valor fica `null` — `''` faria a linha
        // parecer um campo de texto vazio para quem inspecionasse o estado
        value: filter.type === 'boolean' ? null
            : op === 'between' || op === 'between_dates' ? ['', ''] : '',
    });
    const updateValue = (value: any) => onChange({ ...filter, value });
    const applyPreset = (preset: FilterPreset) => {
        const { operator, value } = preset.apply();
        onChange({ ...filter, operator, value });
    };

    /**
     * ⚠️ O booleano ganha o estado neutro AQUI, e não em cada schema.
     *
     * Ele é o único tipo sem campo de valor — o operador É a resposta —, então sem uma opção
     * "não filtrar" a linha existir já significava filtrar. Pôr o `is_unset` em cada schema
     * booleano da aplicação seria uma linha a lembrar toda vez que alguém criar um filtro novo; o
     * componente que sabe da assimetria é este.
     *
     * Sem a opção na lista, o `<select>` ainda mostraria a PRIMEIRA ("sim") com o estado em
     * `is_unset` — a tela dizendo uma coisa e o payload outra.
     */
    const operatorOptions = (): Operator[] => (
        schema.type === 'boolean' && !schema.operators.includes(BOOLEAN_UNSET)
            ? [BOOLEAN_UNSET, ...schema.operators]
            : schema.operators
    );

    const renderOperatorSelect = () => (
        <select
            className="border dark:border-slate-600 rounded px-2 py-1 text-xs dark:bg-slate-700 dark:text-white min-w-[100px]"
            // o rótulo do campo fica num <span> ao lado, não num <label>: sem isto o select não
            // tem nome nenhum para leitor de tela — e num booleano ele É a resposta, não um
            // acessório do campo de valor
            aria-label={`Operador de ${filter.label}`}
            value={filter.operator}
            onChange={e => updateOperator(e.target.value as Operator)}
        >
            {operatorOptions().map(op => (
                <option key={op} value={op}>{operatorLabels[op]}</option>
            ))}
        </select>
    );

    const renderValueInput = () => {
        const baseClass = "border dark:border-slate-600 rounded px-2 py-1 text-xs dark:bg-slate-700 dark:text-white";

        // Boolean — no input needed (operator IS the value)
        if (schema.type === 'boolean') return null;

        const moneyClass = `${baseClass} w-32 text-right`;

        // Between (two inputs)
        if (filter.operator === 'between' || filter.operator === 'between_dates') {
            const [min, max] = Array.isArray(filter.value) ? filter.value : ['', ''];
            if (schema.currency) {
                return (
                    <div className="flex items-center gap-1">
                        <MoneyInput label={`${schema.label} de`} value={min} inputClassName={moneyClass}
                            onAmount={amount => updateValue([fromAmount(amount), max])} />
                        <span className="text-xs text-gray-400">—</span>
                        <MoneyInput label={`${schema.label} até`} value={max} inputClassName={moneyClass}
                            onAmount={amount => updateValue([min, fromAmount(amount)])} />
                    </div>
                );
            }
            const inputType = schema.type === 'date' || filter.operator === 'between_dates' ? 'date' : 'number';
            return (
                <div className="flex items-center gap-1">
                    <input type={inputType} className={`${baseClass} w-28`} value={min} aria-label={`${schema.label} de`}
                        onChange={e => updateValue([e.target.value, max])} placeholder="de" />
                    <span className="text-xs text-gray-400">—</span>
                    <input type={inputType} className={`${baseClass} w-28`} value={max} aria-label={`${schema.label} até`}
                        onChange={e => updateValue([min, e.target.value])} placeholder="até" />
                </div>
            );
        }

        // Enum (select / multi-select)
        if (schema.type === 'enum' && schema.options) {
            if (filter.operator === 'in' || filter.operator === 'not_in') {
                const selected = Array.isArray(filter.value) ? filter.value : filter.value ? [filter.value] : [];
                return (
                    <div className="flex flex-wrap gap-1 max-w-[300px]">
                        {schema.options.map(opt => (
                            <label key={opt.value} className={`px-2 py-0.5 text-xs rounded cursor-pointer border transition-colors ${
                                selected.includes(opt.value)
                                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-200'
                                    : 'bg-gray-50 border-gray-200 text-gray-600 dark:bg-slate-700 dark:border-slate-600 dark:text-gray-400'
                            }`}>
                                <input type="checkbox" className="hidden"
                                    checked={selected.includes(opt.value)}
                                    onChange={() => {
                                        const newVal = selected.includes(opt.value)
                                            ? selected.filter((v: string) => v !== opt.value)
                                            : [...selected, opt.value];
                                        updateValue(newVal);
                                    }} />
                                {opt.label}
                            </label>
                        ))}
                    </div>
                );
            }
            return (
                <select className={baseClass} value={filter.value || ''} onChange={e => updateValue(e.target.value)}>
                    <option value="">—</option>
                    {schema.options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            );
        }

        // Date
        if (schema.type === 'date') {
            return <input type="date" className={`${baseClass} w-40`} value={filter.value || ''} aria-label={schema.label} onChange={e => updateValue(e.target.value)} />;
        }

        // Number
        if (schema.type === 'number') {
            if (schema.currency) {
                return <MoneyInput label={schema.label} value={filter.value} inputClassName={moneyClass}
                    onAmount={amount => updateValue(fromAmount(amount))} />;
            }
            return <input type="number" step="any" className={`${baseClass} w-36`} value={filter.value || ''} aria-label={schema.label} onChange={e => updateValue(e.target.value)} placeholder="valor" />;
        }

        // Text (default)
        return <input type="text" className={`${baseClass} w-48 flex-1`} value={filter.value || ''} aria-label={schema.label} onChange={e => updateValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && onSubmit) { e.preventDefault(); onSubmit(); } }}
            placeholder="buscar..." />;
    };

    return (
        <div className="flex items-center gap-2 py-1.5 px-3 bg-gray-50 dark:bg-slate-700/50 rounded group flex-wrap sm:flex-nowrap">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 min-w-[100px] shrink-0">{schema.label}</span>
            {renderOperatorSelect()}
            {renderValueInput()}
            {/* Atalhos: preenchem operador e valor de uma vez. Ficam DEPOIS do campo, não no lugar
                dele — o valor calculado continua visível e editável à mão. */}
            {schema.presets && schema.presets.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {schema.presets.map(preset => {
                        const active = isPresetActive(preset, filter);
                        return (
                            <button key={preset.label} type="button" title={preset.hint}
                                onClick={() => applyPreset(preset)}
                                className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${active
                                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-200'
                                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-100 dark:bg-slate-800 dark:border-slate-600 dark:text-gray-400 dark:hover:bg-slate-700'
                                }`}>
                                {preset.label}
                            </button>
                        );
                    })}
                </div>
            )}
            <button onClick={onRemove} aria-label={`Remover filtro ${filter.label}`}
                className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" title="Remover filtro">
                <FaTimes size={10} />
            </button>
        </div>
    );
};

export default FilterField;
